/**
 * REFERENCED LIST TYPES
 * The reverse index of entity references: given a record, which documents point at it.
 * Written exclusively by the `docMirror` trigger into the
 * top-level `referencedList` property of the referenced record — a sibling of
 * `entityReferences`, not a member of `fields` — and read by `ReferencedListUI` in `react/`.
 *
 * Invariant: this value is read-only and receive-only on the client. Nothing in the UI
 * writes it, and no forward-reference writer (`useSaveEntityReference`, `useEntityRecordUI`,
 * `ArCell`) is aware it exists — the mirror derives it from those writers' output.
 *
 * Invariant: the value is a MAP of encoded source path to source name, never an array. The
 * key carries every coordinate a reader needs — decode it for the path, take its second
 * segment for the entity id — so the name is the only thing worth storing beside it. A map
 * also lets every update be an independent dotted-path set or delete, with no
 * read-modify-write to lose when writes to one target fan in concurrently.
 *
 * Invariant: records of a declared relation's source entity are the only source. Form versions
 * are deliberately excluded — one version is one document, so a heavily-referenced record would
 * collect an unbounded number of entries and eventually exceed the 1 MiB document ceiling, which
 * would block every write to that record, not just the mirror's. The same ceiling argument is why
 * record sources are admitted per relation, not per entity: see `ReferencedListConfig`.
 *
 * Invariant: which relations exist is admin intent, held in the single Firestore document at
 * `REFERENCED_LIST_CONFIG_PATH`. The trigger, the reconciler, the settings tab and the backfill
 * agree because they parse that one document through the same pure function — not because they
 * share a compiled constant. The parsers, the extractors and the differ live here rather than in
 * `docMirror` because every one of those consumers must derive byte-identical entries onto the
 * same documents and cannot import `docMirror`, which pulls in `#db` and `firebase-functions`.
 * Everything here is pure: no Firestore handle, no I/O, no async — the caller supplies the
 * document. The fold and diff built on top of these live beside them in
 * `System.ReferencedListPlan.types.ts`, for the same reason and one major more: the consumers pin
 * different `firebase-admin` versions.
 */

import { isEntityReference, type EntityId } from './System.Artifacts.types';
import type { RelationshipTreeNode, RelationshipTreeValue } from './System.RelationshipTree.types';

/** The one Firestore document declaring which relations the reverse index maintains. */
export const REFERENCED_LIST_CONFIG_PATH = 'parameters/referencedList';

/**
 * One admitted (source entity → target entity) pair. A Contact points at its organisation through
 * an `entityReference` and the organisation displays the reverse index, so the organisation is the
 * target, not the Contact. An entity may be both: `Contacts` is a source of `Customers` and a
 * target of them.
 */
export type ReferencedListRelation = {
  sourceEntityId: EntityId;
  targetEntityId: EntityId;
};

/**
 * Stable identity of a pair, for React list keys and for looking a declared relation's state up in
 * `ReferencedListReconciliation`. One definition so the settings tab and the reconciler cannot
 * disagree about what names a pair.
 */
export function referencedListRelationKey(relation: ReferencedListRelation): string {
  return `${relation.sourceEntityId}->${relation.targetEntityId}`;
}

/**
 * `backfilling` — declared, entries still being written. `active` — maintained. `retiring` —
 * withdrawn, stranded entries still being stripped. `error` — the reconciler measured a fan-in the
 * index cannot carry, or failed; the pair stops being maintained.
 */
export type ReferencedListRelationStatus = 'backfilling' | 'active' | 'retiring' | 'error';

export type ReferencedListRelationState = {
  sourceEntityId: EntityId;
  targetEntityId: EntityId;
  status: ReferencedListRelationStatus;
  /** Highest entry count the reconciler measured on any one target for this pair. */
  maxFanIn?: number;
  error?: string;
  finishedAt?: string;
};

export type ReferencedListReconciliation = {
  reconciledRevision: number;
  states: ReferencedListRelationState[];
};

/**
 * Admin intent plus the job's answer to it.
 *
 * The unit of declaration is the relation, not the entity, because the 1 MiB document risk is
 * relation-shaped. Measured on the prototype dataset 2026-08-06: `Contacts → Manufacturers` peaks
 * at fan-in 24, while `Products → Manufacturers` is 67,582 references reaching fan-in 8,074 on one
 * Manufacturer record — a measured 584 KB of index on a single document, ~72 B an entry. Same
 * target entity, two orders of magnitude apart by source. A target set alone cannot express that,
 * and a source set crossed with a target set would admit the pair that breaks the document.
 *
 * A reverse index is only safe where the referencing population is organically small — the product
 * intent is a pot of contacts on an organisation. Large relations are forward queries, never stored
 * indexes. `REFERENCED_LIST_MAX_ENTRIES` is a backstop *under* the declaration, not a second bound.
 *
 * What is new here is that an admin can now declare a pair that breaks a document, which is exactly
 * why the reconciler measures fan-in per pair and moves an over-cap pair to `'error'` —
 * `parseReferencedListRelations` then stops maintaining it, whatever the admin declared.
 */
export type ReferencedListConfig = {
  /** Admin intent. Written only by the settings tab. */
  relations: ReferencedListRelation[];
  /** Bumped by the settings tab on every save. */
  revision: number;
  /** Job-owned. Recomputed wholesale by the reconciler; any other writer is overwritten. */
  reconciliation?: ReferencedListReconciliation;
};

/**
 * Config arrives from Firestore, so every field is caller-supplied and is validated rather than
 * trusted. Ids must be non-empty strings; anything else is dropped rather than repaired.
 */
const relationOf = (value: unknown): ReferencedListRelation | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const { sourceEntityId, targetEntityId } = value as Record<string, unknown>;
  if (typeof sourceEntityId !== 'string' || !sourceEntityId) return undefined;
  if (typeof targetEntityId !== 'string' || !targetEntityId) return undefined;
  return { sourceEntityId, targetEntityId };
};

const declaredRelations = (
  config: ReferencedListConfig | undefined,
): readonly ReferencedListRelation[] =>
  Array.isArray(config?.relations)
    ? config.relations.flatMap((entry) => {
        const relation = relationOf(entry);
        return relation ? [relation] : [];
      })
    : [];

const reconciledStates = (
  config: ReferencedListConfig | undefined,
): readonly ReferencedListRelationState[] =>
  Array.isArray(config?.reconciliation?.states) ? config.reconciliation.states : [];

/**
 * The relations the reverse index maintains: declared intent minus every pair the reconciler moved
 * to `'error'`. A pair measured over the cap must stop being maintained even while the admin still
 * declares it — the declaration is intent, this is what the trigger acts on.
 *
 * A `Map` of `Set`s, not nested `Record`s, because membership is tested against caller-supplied
 * entity ids: a source writing `entityId: 'constructor'` resolves truthy on an object index and
 * would silently corrupt a lookup. `Map.get` and `Set.has` have no prototype hazard.
 */
export function parseReferencedListRelations(
  config: ReferencedListConfig | undefined,
): ReadonlyMap<EntityId, ReadonlySet<EntityId>> {
  const rejected = new Set<string>(
    reconciledStates(config).flatMap((state) => {
      const relation = relationOf(state);
      return relation && state.status === 'error' ? [referencedListRelationKey(relation)] : [];
    }),
  );

  const relations = new Map<EntityId, Set<EntityId>>();
  declaredRelations(config).forEach((relation) => {
    if (rejected.has(referencedListRelationKey(relation))) return;
    const targets = relations.get(relation.sourceEntityId) ?? new Set<EntityId>();
    targets.add(relation.targetEntityId);
    relations.set(relation.sourceEntityId, targets);
  });
  return relations;
}

/**
 * The entities that may carry a `referencedList` at all: targets of intent UNION targets of every
 * reconciliation state. Deliberately WIDER than `parseReferencedListRelations` — a withdrawn or
 * rejected pair's target must stay visitable so the reconciler can strip its stranded entries,
 * which is the runtime analogue of the backfill's deliberately-wide `current` scan. Membership says
 * nothing about whether an entity is also a source.
 */
export function parseReferencedListTargets(
  config: ReferencedListConfig | undefined,
): ReadonlySet<EntityId> {
  return new Set<EntityId>([
    ...declaredRelations(config).map((relation) => relation.targetEntityId),
    ...reconciledStates(config).flatMap((state) => {
      const relation = relationOf(state);
      return relation ? [relation.targetEntityId] : [];
    }),
  ]);
}

/**
 * The most entries one target's `referencedList` may hold. At ~72 B per entry — the rate implied by
 * the measured 584 KB over 8,074 entries above — that is ~36 KB, ~3.5% of Firestore's 1 MiB document
 * ceiling and 4x the measured maximum fan-in of 114 on real data. A count, not a byte budget:
 * entries are uniform, so measuring bytes buys no precision at 4x headroom and costs a serialisation
 * on every write.
 *
 * This is a backstop against a data shape nobody predicted pushing a record past the ceiling and
 * making it unwritable by anything, not just the mirror. The primary bound is the declaration in
 * `ReferencedListConfig`. The trigger (`docMirror`), the reconciler
 * (`referencedListReconcile`) and the backfill CLI (`backfillReferencedList`) all enforce it and all report every target
 * they cap — the index is incomplete from that point on, which is a declaration bug to fix, not a
 * steady state to live in.
 *
 * The count is over the stored map, not over the declared relations. Every key in `referencedList`
 * counts, including one written by a source whose relation is no longer declared, and the trigger's
 * relation guard means the trigger can never retire those: a `Products` write returns before
 * extraction. Left alone, such a target would sit above this cap and reject every legitimate add
 * indefinitely. What clears it is the reconciler, which the config write itself schedules: it plans
 * against the deliberately wide target union, so a stranded key is deleted at its own field path on
 * the same pass that withdrew its relation, with no ordering to observe against the trigger. The
 * backfill CLI runs that same derivation on demand, for a migration that wants a rehearsal.
 */
export const REFERENCED_LIST_MAX_ENTRIES = 500;

/**
 * Keyed by `encodeReferencedListKey(sourcePath)`, valued by the source document's name —
 * empty when the source has none, in which case readers fall back to the decoded path. One
 * source document occupies exactly one slot on a given target, however many of its fields
 * happen to point there.
 */
export type ReferencedListValue = { [encodedSourcePath: string]: string };

/**
 * Firestore map keys may not contain `/`, and a `.` in a key breaks dotted-path updates, so
 * neither may survive encoding. `~` is the escape character and escapes itself, which is what
 * makes the scheme injective: every `~` in an encoded key introduces exactly one two-character
 * escape, so decoding is one unambiguous left-to-right pass. Encoding as a single pass matters
 * too — sequential global replaces would re-escape their own output.
 */
const REFERENCED_LIST_KEY_ESCAPES: Record<string, string> = { '~': '~~', '/': '~s', '.': '~d' };
const REFERENCED_LIST_KEY_UNESCAPES: Record<string, string> = { '~': '~', s: '/', d: '.' };

export function encodeReferencedListKey(sourcePath: string): string {
  return sourcePath.replace(/[~/.]/g, (character) => REFERENCED_LIST_KEY_ESCAPES[character]);
}

export function decodeReferencedListKey(encodedKey: string): string {
  return encodedKey.replace(
    /~(.)/g,
    (escape, character: string) => REFERENCED_LIST_KEY_UNESCAPES[character] ?? escape,
  );
}

/**
 * Key of an extraction map: the referenced record plus the entity-or-group id the source wrote
 * alongside it. Both halves are Firestore ids, so the first `/` is always the separator.
 */
export function referenceTargetKey(hint: string | undefined, recordId: string): string {
  return `${hint ?? ''}/${recordId}`;
}

export function parseReferenceTargetKey(key: string): { hint?: string; recordId: string } {
  const separator = key.indexOf('/');
  const hint = key.slice(0, separator);
  return { ...(hint ? { hint } : {}), recordId: key.slice(separator + 1) };
}

/**
 * The label a source record is listed under. It lives on the source document itself, so a
 * rename re-fires that document's own trigger and the new label rides the ordinary diff —
 * there is no separate rename pass. Empty when the record is unnamed.
 */
const documentName = (data: Record<string, unknown>): string =>
  typeof data.name === 'string' ? data.name : '';

/**
 * Walks one `entityReferences` map. Callers pass either the document itself — the top-level map
 * every record and form version carries — or a `CompositeFieldValue`, whose map is scoped to that
 * one field but is just as live.
 */
export function extractEntityReferences(
  data: Record<string, unknown>,
  name: string,
): Map<string, string> {
  const extracted = new Map<string, string>();
  const references = data.entityReferences;
  if (!references || typeof references !== 'object') return extracted;

  Object.entries(references as Record<string, unknown>).forEach(([viaKey, reference]) => {
    if (!reference || typeof reference !== 'object') return;
    const { entityRecordId, entityId } = reference as { entityRecordId?: unknown; entityId?: unknown };
    if (typeof entityRecordId !== 'string' || !entityRecordId) return;
    const hint = typeof entityId === 'string' && entityId ? entityId : viaKey;
    extracted.set(referenceTargetKey(hint, entityRecordId), name);
  });
  return extracted;
}

const isRelationshipTreeValue = (value: unknown): value is RelationshipTreeValue =>
  !!value && typeof value === 'object' && Array.isArray((value as { trees?: unknown }).trees);

const flattenTreeNodes = (nodes: readonly RelationshipTreeNode[]): RelationshipTreeNode[] =>
  nodes.flatMap((node) => [
    node,
    ...flattenTreeNodes(Array.isArray(node?.children) ? node.children : []),
  ]);

/**
 * Walks every relationship-tree field value. Nodes that have not had a record selected yet
 * carry no `entityRecordId` and reference nothing.
 */
export function extractRelationshipTreeReferences(
  data: Record<string, unknown>,
  name: string,
): Map<string, string> {
  const extracted = new Map<string, string>();
  const fields = data.fields;
  if (!fields || typeof fields !== 'object') return extracted;

  Object.values(fields as Record<string, unknown>).forEach((field) => {
    if (!field || typeof field !== 'object') return;
    const { value } = field as { value?: unknown };
    if (!isRelationshipTreeValue(value)) return;
    flattenTreeNodes(value.trees).forEach((node) => {
      if (typeof node?.entityRecordId !== 'string' || !node.entityRecordId) return;
      extracted.set(referenceTargetKey(node.entityOrGroupId, node.entityRecordId), name);
    });
  });
  return extracted;
}

/**
 * Walks the `entityReferences` map a `CompositeFieldValue` carries at `fields.<id>.value`. That
 * map is a live forward reference scoped to the composite field, written by
 * `CompositeFieldProvider.setEntityReference`, so it belongs in the reverse index.
 *
 * An `ArEntityReference` cell value sits at the same address and also carries an
 * `entityReferences` key, but that one is a point-in-time snapshot of the *referenced* record's
 * own references — mirroring it would credit the host with references it does not hold. The
 * `referenceType: 'entity'` discriminant is the only thing separating the two shapes, so it is
 * checked rather than inferred.
 */
export function extractCompositeFieldReferences(
  data: Record<string, unknown>,
  name: string,
): Map<string, string> {
  const extracted = new Map<string, string>();
  const fields = data.fields;
  if (!fields || typeof fields !== 'object') return extracted;

  Object.values(fields as Record<string, unknown>).forEach((field) => {
    if (!field || typeof field !== 'object') return;
    const { value } = field as { value?: unknown };
    if (!value || typeof value !== 'object' || isEntityReference(value)) return;
    extractEntityReferences(value as Record<string, unknown>, name).forEach((entry, key) => {
      extracted.set(key, entry);
    });
  });
  return extracted;
}

/**
 * Every reference a host record holds: the top-level `entityReferences` map, the
 * `entityReferences` map inside each composite field value, and every relationship-tree node. A
 * tree node and a reference field pointing at the same record under the same entity id collapse
 * to one entry — the target's map is keyed by source path, so one source document can only ever
 * occupy one slot on a given target.
 *
 * Not walked, deliberately: the `entityReferences` snapshot nested in an `ArEntityReference` cell
 * value, which belongs to the referenced record rather than the host.
 *
 * Not covered, and not by choice: `ArEntityReference` cells living in ArTable row subcollections
 * (`entities/{e}/records/{r}/_art_<tableId>/{rowId}`) hold live references the mirror never sees,
 * because its trigger watches the record document alone. Those rows are absent from this map.
 */
export function extractHostReferences(data: Record<string, unknown>): Map<string, string> {
  const name = documentName(data);
  return new Map([
    ...extractEntityReferences(data, name),
    ...extractCompositeFieldReferences(data, name),
    ...extractRelationshipTreeReferences(data, name),
  ]);
}

export type ReferenceDelta = {
  adds: Map<string, string>;
  removes: Map<string, string>;
};

/**
 * A key present on both sides under a different name counts as an add: the write is an
 * overwrite of the same map slot, which is also how a host rename propagates.
 */
export function diffReferences(
  before: Map<string, string>,
  after: Map<string, string>,
): ReferenceDelta {
  const adds = new Map<string, string>();
  after.forEach((name, key) => {
    if (before.get(key) !== name) adds.set(key, name);
  });

  const removes = new Map<string, string>();
  before.forEach((name, key) => {
    if (!after.has(key)) removes.set(key, name);
  });

  return { adds, removes };
}
